import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, Building2, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanySwitcher = () => {
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/my-companies`);
      setCompanies(response.data.companies || []);
      setActiveCompany(response.data.active_company);
      
      // Store in localStorage for persistence
      if (response.data.active_company) {
        localStorage.setItem('activeCompanyId', response.data.active_company.id);
      }
    } catch (error) {
      console.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const switchCompany = async (company) => {
    try {
      await axios.post(`${API}/portal/switch-company`, {
        company_id: company.id
      });
      
      setActiveCompany(company);
      localStorage.setItem('activeCompanyId', company.id);
      setOpen(false);
      
      toast.success(`Switched to ${company.company_name}`);
      
      // Reload page to refresh company-scoped data
      window.location.reload();
      
    } catch (error) {
      toast.error('Failed to switch company');
    }
  };

  if (loading || !activeCompany) {
    return (
      <div className="px-3 py-2 text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }

  // If only one company, just show it (no dropdown needed)
  if (companies.length === 1) {
    return (
      <div className="px-3 py-2 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          <div>
            <div className="text-xs text-muted-foreground">Managing</div>
            <div className="text-sm font-semibold text-foreground">{activeCompany.company_name}</div>
          </div>
          {activeCompany.abn_verified && (
            <CheckCircle className="w-4 h-4 text-green-600 ml-auto" title="ABN Verified" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 bg-muted/50 hover:bg-muted rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          <div className="flex-1 text-left">
            <div className="text-xs text-muted-foreground">Managing</div>
            <div className="text-sm font-semibold text-foreground">{activeCompany.company_name}</div>
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setOpen(false)}
          />
          <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
            {companies.map((company) => (
              <button
                key={company.id}
                onClick={() => switchCompany(company)}
                className={`w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0 ${
                  company.id === activeCompany.id ? 'bg-muted/50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-foreground">{company.company_name}</div>
                    {company.industry && (
                      <div className="text-xs text-muted-foreground">{company.industry}</div>
                    )}
                  </div>
                  {company.abn_verified ? (
                    <CheckCircle className="w-4 h-4 text-green-600" title="ABN Verified" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-yellow-600" title="ABN Not Verified" />
                  )}
                </div>
              </button>
            ))}
            
            {companies.length < 3 && (
              <button
                onClick={() => {
                  setOpen(false);
                  // Navigate to create company
                  window.location.href = '/portal/companies/new';
                }}
                className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-t-2 border-border"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-sm font-semibold text-primary">
                    Add Company ({companies.length}/3)
                  </div>
                </div>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CompanySwitcher;
